import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import PasswordInput from '../components/PasswordInput';
import { supabase } from '../supabase';
import { registerOrganization, signOutSafely } from '../lib/auth';
import { useAuth } from '../authContext';
import PublicNav from '../components/PublicNav';
import { theme } from '../lib/theme';

const ORG_TYPES = ['School', 'Church', 'Company', 'Sports team', 'Community', 'Other'];

const inp = {
  width: '100%',
  boxSizing: 'border-box',
  background: theme.bg2,
  border: '1px solid rgba(255,255,255,0.08)',
  color: theme.text,
  borderRadius: 10,
  padding: '12px 14px',
  fontSize: 15,
  outline: 'none',
};

export default function OrgRegister() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  const [org, setOrg] = useState({
    name: '',
    slug: '',
    orgType: 'School',
    city: '',
    country: 'Nigeria',
    estimatedPassengers: '',
    useCaseNotes: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });

  const [account, setAccount] = useState({ password: '', confirm: '' });

  const setOrgField = (k) => (e) => setOrg((o) => ({ ...o, [k]: e.target.value }));

  const slugFromName = (name) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (account.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (account.password !== account.confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { data: signUp, error: signErr } = await supabase.auth.signUp({
        email: org.contactEmail.trim(),
        password: account.password,
      });
      if (signErr) throw signErr;
      if (!signUp.session) {
        throw new Error('Confirm your email in Supabase settings, or disable email confirmation for development.');
      }

      const { orgId, inviteCode: code, error: orgErr } = await registerOrganization({
        name: org.name,
        slug: org.slug || slugFromName(org.name),
        contactName: org.contactName,
        contactEmail: org.contactEmail,
        contactPhone: org.contactPhone,
        city: org.city,
        orgType: org.orgType,
        country: org.country,
        estimatedPassengers: org.estimatedPassengers ? parseInt(org.estimatedPassengers, 10) : null,
        useCaseNotes: org.useCaseNotes,
      });

      if (orgErr) {
        await signOutSafely();
        throw orgErr;
      }
      setInviteCode(code || '');
      setStep(2);
      await refresh();
      if (orgId) {
        setTimeout(() => navigate('/admin', { replace: true }), 4000);
      }
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setBusy(false);
    }
  }

  if (step === 2) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font, padding: 24 }}>
        <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
          <h2 style={{ fontWeight: 800, marginBottom: 12 }}>Organisation created</h2>
          <p style={{ color: theme.muted, lineHeight: 1.6 }}>
            Share this invite code with drivers and passengers:
          </p>
          <p style={{ fontSize: 28, fontWeight: 800, color: theme.accentLight, letterSpacing: '0.1em', margin: '20px 0' }}>{inviteCode}</p>
          <p style={{ fontSize: 14, color: theme.muted }}>
            Registration link: <code>/register/{org.slug || slugFromName(org.name)}</code>
          </p>
          <p style={{ marginTop: 24, fontSize: 14, color: theme.dim }}>Redirecting to admin dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text, fontFamily: theme.font }}>
      <PublicNav />
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '96px 24px 80px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 8 }}>Register your organisation</h1>
        <p style={{ color: theme.muted, marginBottom: 28, fontSize: 14 }}>
          Set up team transport for buses, routes, and daily attendance.
        </p>
        {error && <p style={{ color: '#f87171', marginBottom: 16 }}>{error}</p>}

        <form onSubmit={handleSubmit}>
          {step === 0 && (
            <>
              <label style={{ fontSize: 13, color: theme.muted }}>Organisation name *</label>
              <input required value={org.name} onChange={setOrgField('name')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>URL slug *</label>
              <input
                required
                value={org.slug}
                onChange={setOrgField('slug')}
                placeholder={slugFromName(org.name) || 'my-school'}
                style={{ ...inp, marginBottom: 14 }}
              />
              <label style={{ fontSize: 13, color: theme.muted }}>Type *</label>
              <select value={org.orgType} onChange={setOrgField('orgType')} style={{ ...inp, marginBottom: 14 }}>
                {ORG_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <label style={{ fontSize: 13, color: theme.muted }}>City *</label>
              <input required value={org.city} onChange={setOrgField('city')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>Estimated passengers</label>
              <input type="number" min="1" value={org.estimatedPassengers} onChange={setOrgField('estimatedPassengers')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>How will you use Transport Team?</label>
              <textarea value={org.useCaseNotes} onChange={setOrgField('useCaseNotes')} rows={3} style={{ ...inp, marginBottom: 20, resize: 'vertical' }} />
              <button type="button" onClick={() => setStep(1)} style={{ width: '100%', padding: 14, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
                Next: admin account
              </button>
            </>
          )}

          {step === 1 && (
            <>
              <label style={{ fontSize: 13, color: theme.muted }}>Admin full name *</label>
              <input required value={org.contactName} onChange={setOrgField('contactName')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>Admin email *</label>
              <input required type="email" value={org.contactEmail} onChange={setOrgField('contactEmail')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>Admin phone *</label>
              <input required value={org.contactPhone} onChange={setOrgField('contactPhone')} style={{ ...inp, marginBottom: 14 }} />
              <label style={{ fontSize: 13, color: theme.muted }}>Password *</label>
              <PasswordInput
                required
                minLength={8}
                value={account.password}
                onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))}
                style={{ marginBottom: 14 }}
                inputStyle={inp}
                autoComplete="new-password"
                toggleColor={theme.muted}
              />
              <label style={{ fontSize: 13, color: theme.muted }}>Confirm password *</label>
              <PasswordInput
                required
                value={account.confirm}
                onChange={(e) => setAccount((a) => ({ ...a, confirm: e.target.value }))}
                style={{ marginBottom: 8 }}
                inputStyle={inp}
                autoComplete="new-password"
                toggleColor={theme.muted}
              />
              <ForgotPasswordLink scope="team" style={{ marginBottom: 16 }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setStep(0)} style={{ flex: 1, padding: 12, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: theme.text, borderRadius: 10, cursor: 'pointer' }}>
                  Back
                </button>
                <button type="submit" disabled={busy} style={{ flex: 2, padding: 12, background: theme.accent, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
                  {busy ? 'Creating…' : 'Create organisation'}
                </button>
              </div>
            </>
          )}
        </form>

        <p style={{ marginTop: 24, textAlign: 'center', fontSize: 14, color: theme.muted }}>
          Already registered? <Link to="/login" style={{ color: theme.accentLight }}>Team sign in</Link>
        </p>
      </div>
    </div>
  );
}
